import React, { useState, useMemo, useCallback } from "react";
import { node, number, shape, string } from "prop-types";
import { Box, Stack, Typography } from "@mui/material";
import { videoArrayType, documentArrayType } from "../../../constants/proptypes";
import compact from "lodash/compact";

import VideoDialog from "../../VideoDialog";
import VideoCard from "../../VideoCard";
import { ReportSnapshotHeading } from "./ReportSnapshotHeading";
import { ReportSnapshotDocuments } from "./ReportSnapshotDocuments";
import DrugList from "../../DrugList";

export const ReportSnapshotSection = ({ children, title, mb = 4 }) => (
  <Box sx={{ mb }}>
    <ReportSnapshotHeading text={title} />
    {children}
  </Box>
);

ReportSnapshotSection.propTypes = {
  children: node.isRequired,
  title: string.isRequired,
  mb: number,
};

const ReportSnapshotBody = ({ report }) => {
  // Add class to
  const pageContainer = document.getElementById("icer-spa");
  pageContainer && pageContainer.classList.add("body-flex");

  const [modalOpen, setModalOpen] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState(null);

  const [drugs, documents] = useMemo(() => [report.drugs, compact(report.documents)], [report]);
  const featuredVideo = useMemo(
    () => report.videos.find(({ field_name }) => field_name === "youtube_id_1"),
    [report.videos],
  );
  const relatedVideos = useMemo(
    () => report.videos.filter(({ field_name }) => field_name !== "youtube_id_1"),
    [report.videos],
  );

  const toggleModal = useCallback(() => setModalOpen(!modalOpen), [modalOpen]);
  const activateVideo = useCallback((videoId) => {
    setCurrentVideoId(videoId);
    setModalOpen(true);
  }, []);

  const hasDocuments = compact(documents).length > 0;
  const hasRelatedVideos = relatedVideos.length > 0;
  const hasRightColumn = hasDocuments || hasRelatedVideos;

  return (
    <Box id="details-body" sx={{ p: 3 }}>
      <Stack
        direction="row"
        spacing={6}
        sx={{
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            flex: {
              xs: "1 1 100%",
              lg: hasRightColumn ? "1 1 calc(50% - 24px)" : "1 1 50%",
            },
            minWidth: 0,
            maxWidth: hasRightColumn ? undefined : "50%",
          }}
        >
          {featuredVideo && (
            <ReportSnapshotSection title="Featured Video">
              <VideoCard
                video={{ youtube_id: featuredVideo.id, ...featuredVideo }}
                showDetails={false}
                handleClick={() => activateVideo(featuredVideo.id)}
                isFeatured
              />
            </ReportSnapshotSection>
          )}
          <ReportSnapshotSection title="Drugs in Report">
            {drugs.length > 0 ? (
              <DrugList drugs={drugs} darkChips={true} showTitle={false} />
            ) : (
              <Typography variant="body1">No drugs associated with this report.</Typography>
            )}
          </ReportSnapshotSection>
        </Box>
        {hasRightColumn && (
          <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 calc(50% - 24px)" }, minWidth: 0 }}>
            {hasDocuments && (
              <ReportSnapshotDocuments
                documents={documents}
                calloutText={report.recommendation_callout}
              />
            )}
            {hasRelatedVideos && (
              <ReportSnapshotSection title="Related Videos">
                <Stack
                  direction="row"
                  sx={{
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  {relatedVideos.map((v, i) => (
                    <Box
                      key={`related-video-${i}`}
                      sx={{
                        flex: {
                          xs: "0 0 100%",
                          lg: "0 0 calc(50% - 8px)",
                        },
                        display: "flex",
                        alignItems: "stretch",
                        minWidth: 0,
                      }}
                    >
                      <VideoCard
                        video={{ youtube_id: v.id, ...v }}
                        variant="horizontal"
                        handleClick={() => activateVideo(v.id)}
                      />
                    </Box>
                  ))}
                </Stack>
              </ReportSnapshotSection>
            )}
          </Box>
        )}
      </Stack>
      <VideoDialog
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        video={{ id: currentVideoId, youtube_id: currentVideoId }}
      />
    </Box>
  );
};

ReportSnapshotBody.defaultProps = {
  report: null,
};

ReportSnapshotBody.propTypes = {
  report: shape({
    videos: videoArrayType.isRequired,
    documents: documentArrayType.isRequired,
  }),
};

export default ReportSnapshotBody;
